import { create } from 'zustand';

import { historyRepo } from '../../data/repositories';
import {
  clear,
  createComposer,
  deleteChar,
  deleteWord,
  insert,
  insertTyped,
  isEmpty,
  moveCaret,
  recaseWordBeforeCaret,
  replaceRange,
  undo,
  utterance,
  wordBeforeCaret,
  type ComposerState,
} from './composer';
import type { UtteranceSource } from '../types';

/**
 * React binding for the utterance buffer. All semantics live in `composer.ts`; this only holds
 * the current state and talks to storage.
 *
 * `commit()` deliberately does **not** emit `UtteranceSpoken`. `specs/10-contracts.md` says it does,
 * but that event carries `providerId`, which only `SpeechService` knows once a provider has
 * actually succeeded — so emitting here would either mean guessing or reporting speech that
 * never happened. `SpeechService` emits it; this records history and clears the buffer.
 */
/** What the output bar highlights briefly after an abbreviation expanded. */
export interface LastExpansion {
  code: string;
  startIndex: number;
  endIndex: number;
}

interface ComposerStore {
  state: ComposerState;
  lastExpansion: LastExpansion | null;
  /**
   * The last utterance written to history, and only consulted on the `keepText` path.
   *
   * Tapping `Sprechen` three times to hear how a sentence sounds must not write three identical
   * history rows: history feeds Phase 2's personal n-grams, so it would teach the scorer that this
   * person's commonest utterance is whatever they were checking the prosody of. On the clearing path
   * the buffer is empty afterwards, so the question cannot arise.
   */
  lastCommitted: string | null;
  insert: (text: string) => void;
  /** Insert a keystroke, cased as German. Every keyboard uses this; nothing else should. */
  insertTyped: (key: string) => void;
  /** Apply the corpus spelling of the word before the caret, if it differs by case alone. */
  recaseWord: (canonical: string) => void;
  deleteChar: () => void;
  deleteWord: () => void;
  clear: () => void;
  undo: () => void;
  moveCaret: (caret: number) => void;
  /**
   * Try to expand the word before the caret. The expander is injected rather than imported so
   * the composer keeps no dependency on storage.
   */
  tryExpandAbbrev: (
    expand: (text: string, caret: number) => Promise<{
      code: string;
      expansion: string;
      startIndex: number;
      text: string;
      caret: number;
    } | null>,
  ) => Promise<void>;
  /**
   * Replace the partly typed word with an accepted prediction, plus the space that follows it.
   *
   * One undo step, like an abbreviation expansion: a wrongly accepted candidate must cost exactly one
   * action to reverse, not one per character it inserted. The trailing space is included because the
   * word is finished — making the person tap space afterwards would give back a keystroke the
   * prediction just saved.
   */
  acceptCompletion: (word: string) => void;
  /**
   * Persist to history and empty the buffer. Call after speech has been dispatched.
   *
   * `keepText` leaves the sentence standing instead (`keepTextAfterSpeak`, off by default). A tester
   * spoke one word to hear it, found the word gone, and had to retype it to build a sentence — so the
   * clearing is a setting rather than a rule. History is written either way: what was said belongs in
   * history whatever happens to the buffer.
   */
  commit: (source: UtteranceSource, opts?: { keepText?: boolean }) => Promise<void>;
  text: () => string;
  isEmpty: () => boolean;
  wordBeforeCaret: () => string;
}

export const useComposerStore = create<ComposerStore>((set, get) => ({
  state: createComposer(),
  lastExpansion: null,
  lastCommitted: null,

  insert: (text) => set((s) => ({ state: insert(s.state, text), lastExpansion: null })),
  insertTyped: (key) => set((s) => ({ state: insertTyped(s.state, key), lastExpansion: null })),
  recaseWord: (canonical) =>
    set((s) => ({ state: recaseWordBeforeCaret(s.state, canonical), lastExpansion: null })),
  deleteChar: () => set((s) => ({ state: deleteChar(s.state), lastExpansion: null })),
  deleteWord: () => set((s) => ({ state: deleteWord(s.state), lastExpansion: null })),
  clear: () => set((s) => ({ state: clear(s.state), lastExpansion: null, lastCommitted: null })),
  undo: () => set((s) => ({ state: undo(s.state), lastExpansion: null })),
  moveCaret: (caret) => set((s) => ({ state: moveCaret(s.state, caret) })),

  acceptCompletion: (word) => {
    const { state } = get();
    const partial = wordBeforeCaret(state);
    const start = state.caret - partial.length;
    set({
      state: replaceRange(state, start, state.caret, `${word} `),
      lastExpansion: null,
    });
  },

  tryExpandAbbrev: async (expand) => {
    const { state } = get();
    const result = await expand(state.text, state.caret);
    if (!result) return;

    // The buffer may have moved on while the lookup awaited — the person keeps typing. Expanding
    // stale text would rewrite a word they have already left behind.
    if (get().state.text !== state.text) return;

    set({
      // One undo step for the whole replacement, so a wrong expansion costs one action.
      state: replaceRange(state, result.startIndex, result.startIndex + result.code.length, result.expansion),
      // The confirmation is the sentence appearing in the output bar; the highlight only makes
      // clear *which* part appeared, so an expansion is never a surprise (1.11).
      lastExpansion: {
        code: result.code,
        startIndex: result.startIndex,
        endIndex: result.startIndex + result.expansion.length,
      },
    });
  },

  commit: async (source, opts) => {
    const text = utterance(get().state);
    if (text === '') return;

    const keepText = opts?.keepText ?? false;
    // Read before the write below overwrites it.
    const alreadyInHistory = keepText && get().lastCommitted === text;

    if (keepText) {
      set((s) => {
        // A space, so the next word does not merge into the one just spoken: "Hallo", speak, "du"
        // must not become "Hallodu", which would defeat the point of keeping the text. Only when the
        // caret is already at the end — the repair row moves it, and inserting a space in the middle
        // of a sentence somebody is editing would be a different kind of surprise. `utterance()`
        // trims, so the trailing space never reaches a provider.
        const atEnd = s.state.caret === s.state.text.length;
        const needsSpace = atEnd && !/\s$/u.test(s.state.text);
        return {
          state: needsSpace ? insert(s.state, ' ') : s.state,
          lastExpansion: null,
          lastCommitted: text,
        };
      });
    } else {
      // Cleared first, so the next sentence can start immediately. A person who has just been
      // heard should not wait on a database write to begin the next thought.
      set((s) => ({ state: clear(s.state), lastCommitted: null }));
    }

    if (alreadyInHistory) return;

    try {
      await historyRepo.add(text, source);
    } catch (err) {
      // History is a convenience; losing an entry must never cost the utterance itself.
      console.error('[composer] could not record history', err);
    }
  },

  text: () => utterance(get().state),
  isEmpty: () => isEmpty(get().state),
  wordBeforeCaret: () => wordBeforeCaret(get().state),
}));
